import type { ToolDefinition } from "@arcadeai/arcadejs/resources/index.mjs";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tooltip";
import { getToolkitIcon } from "@arcadeai/design-system/metadata/toolkit-icons";
import { TOOLKIT_CATALOGUE } from "@arcadeai/design-system/metadata/toolkits";
import type { ColumnDef } from "@tanstack/react-table";
import { DataTableColumnHeader } from "@/components/custom/table/data-table-column-header";
import { ParametersPopover } from "../components/parameters-popover";
import {
  expandCamelCase,
  getSearchTerms,
  includesAll,
} from "../lib/search-utils";
import type { Tool } from "../types";
import { DataTableRowActions } from "./data-table-row-actions";
import { RequirementsCell } from "./requirements-cell";

export const columns: ColumnDef<Tool>[] = [
  {
    id: "name",
    accessorKey: "name",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Name" />
    ),
    filterFn: (row, id, value) => {
      if (!value) {
        return true;
      }
      // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
      const name = (row.getValue(id) as string) || "";
      const toolkitName = row.original.toolkit?.name || "";
      const terms = getSearchTerms(value);

      return includesAll(
        [
          name.toLowerCase(),
          expandCamelCase(name),
          toolkitName.toLowerCase(),
          expandCamelCase(toolkitName),
        ],
        terms
      );
    },
  },
  {
    id: "description",
    accessorKey: "description",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Description" />
    ),
    cell: ({ row }) => {
      const description = row.original.description ?? "";
      const firstLine = description.split("\n")[0];
      const isTruncated = firstLine.length > 60;
      const displayText = isTruncated
        ? `${firstLine.slice(0, 60)}...`
        : firstLine;

      if (!isTruncated) {
        return (
          <span className="line-clamp-1 max-w-lg overflow-hidden text-ellipsis whitespace-nowrap">
            {firstLine}
          </span>
        );
      }

      return (
        <Tooltip>
          <TooltipTrigger
            render={
              <span className="line-clamp-1 max-w-lg cursor-help overflow-hidden text-ellipsis whitespace-nowrap" />
            }
          >
            {displayText}
          </TooltipTrigger>
          <TooltipContent className="max-w-sm">
            <p className="text-sm">{description}</p>
          </TooltipContent>
        </Tooltip>
      );
    },
  },
  {
    id: "toolkit",
    accessorKey: "toolkit.name",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Toolkit" />
    ),
    // Only used for the faceted filter; the free-text search runs in the name column
    filterFn: (row, _id, value) => {
      if (Array.isArray(value)) {
        const toolkitName = row.original.toolkit?.name;
        if (!toolkitName) {
          return false;
        }
        return value.includes(toolkitName);
      }
      return true;
    },
    cell: ({ row }) => {
      const toolkitValue = row.getValue("toolkit");
      const toolkit =
        typeof toolkitValue === "string"
          ? TOOLKIT_CATALOGUE[toolkitValue]
          : null;

      if (!toolkit) {
        return row.original.toolkit.name;
      }

      const ToolkitIcon = getToolkitIcon(toolkit.id);
      return (
        <div className="flex items-center gap-2.5">
          <ToolkitIcon className="size-3.5" />
          <span>{toolkit.label}</span>
        </div>
      );
    },
  },
  {
    id: "version",
    accessorKey: "toolkit.version",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Version" />
    ),
  },
  {
    id: "parameters",
    accessorFn: (row) => row.input?.parameters?.length ?? 0,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Params" />
    ),
    cell: ({ row }) => {
      const parameters = row.original.input?.parameters ?? [];
      return <ParametersPopover parameters={parameters} />;
    },
  },
  {
    id: "requirements",
    accessorKey: "requirements",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Requirements" />
    ),
    cell: ({ row }) => (
      <RequirementsCell requirements={row.original.requirements} />
    ),
    filterFn: (row, id, value) => {
      // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
      const requirements = row.getValue(id) as ToolDefinition.Requirements;

      // Handle case where there are no requirements
      if (!requirements) {
        return value.includes("none");
      }

      const { authorization, secrets } = requirements;

      // Handle case where there's no authorization and no secrets
      if (!(authorization || secrets)) {
        return value.includes("none");
      }

      // Handle secrets
      if (secrets && secrets.length > 0) {
        // Check if any of the selected values match the secret keys
        return secrets.some((secret) => value.includes(secret.key));
      }

      // Handle authorization
      if (authorization) {
        if (authorization.provider_id) {
          return value.includes(authorization.provider_id);
        }
        if (authorization.provider_type) {
          return value.includes(authorization.provider_type);
        }
      }

      return false;
    },
  },
  // Hidden column for auth providers (for filtering only)
  {
    id: "authProviders",
    accessorFn: (row) => row.requirements?.authorization?.provider_id || null,
    enableHiding: true,
    filterFn: (row, id, value) => {
      const providerId = row.getValue(id);
      if (!providerId) {
        return false;
      }
      return value.includes(providerId);
    },
  },
  // Hidden column for secrets (for filtering only)
  {
    id: "secrets",
    accessorFn: (row) =>
      row.requirements?.secrets?.map((secret) => secret.key) || [],
    enableHiding: true,
    filterFn: (row, id, value) => {
      // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
      const secretKeys = row.getValue(id) as string[];
      if (!secretKeys.length) {
        return false;
      }
      return secretKeys.some((key) => value.includes(key));
    },
  },
  {
    id: "actions",
    header: "Actions",
    cell: ({ row }) => <DataTableRowActions row={row} />,
  },
];
